import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const GALLERY = [
  { src: "/media/gallery/seatech-1.jpg", title: "180 Gallon", subtitle: "Two-Side Viewable" },
  { src: "/media/gallery/seatech-2.jpg", title: "140 Gallon", subtitle: "Corner Reef Tank" },
  { src: "/media/gallery/seatech-3.jpg", title: "260 Gallon", subtitle: "Swim Tank" },
  { src: "/media/gallery/seatech-4.jpg", title: "75 Gallon", subtitle: "Corner Reef" },
  { src: "/media/gallery/seatech-5.jpg", title: "335 Gallon", subtitle: "Swim Tank" },
  { src: "/media/gallery/seatech-6.jpg", title: "250 Gallon", subtitle: "Swim Tank" },
  { src: "/media/gallery/seatech-7.jpg", title: "165 Gallon", subtitle: "Swim Tank" },
  { src: "/media/gallery/seatech-8.jpg", title: "190 Gallon", subtitle: "Salt Water" },
  { src: "/media/gallery/seatech-9.jpg", title: "120 Gallon", subtitle: "Curved Corner Reef" },
  { src: "/media/gallery/seatech-10.jpg", title: "335 Gallon", subtitle: "Swim Tank" },
];

export const Services = () => {
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, resumeAt: 0, moved: false });
  const [active, setActive] = useState(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf;
    const speed = 0.55;

    const half = () => el.scrollWidth / 2;
    const wrap = () => {
      const h = half();
      if (h <= 0) return;
      if (el.scrollLeft >= h) el.scrollLeft -= h;
      else if (el.scrollLeft < 0) el.scrollLeft += h;
    };

    const step = () => {
      const now = performance.now();
      if (!drag.current.down && active === null && now >= drag.current.resumeAt) {
        el.scrollLeft += speed;
      }
      wrap();
      raf = requestAnimationFrame(step);
    };

    el.scrollLeft = 1;
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  const onDown = (e) => {
    const el = trackRef.current;
    drag.current.down = true;
    drag.current.moved = false;
    drag.current.isMouse = e.pointerType === "mouse";
    drag.current.startX = e.clientX;
    drag.current.startScroll = el.scrollLeft;
    drag.current.pointerId = e.pointerId;
  };

  const onMove = (e) => {
    if (!drag.current.down) return;
    if (Math.abs(e.clientX - drag.current.startX) > 8) drag.current.moved = true;
    // Touch / pen: let the browser handle native momentum scrolling
    if (!drag.current.isMouse) return;
    const el = trackRef.current;
    if (drag.current.moved && !drag.current.captured) {
      drag.current.captured = true;
      el.setPointerCapture?.(drag.current.pointerId);
    }
    if (!drag.current.moved) return;
    const h = el.scrollWidth / 2;
    let next = drag.current.startScroll - (e.clientX - drag.current.startX);
    if (next >= h) { next -= h; drag.current.startScroll -= h; }
    else if (next < 0) { next += h; drag.current.startScroll += h; }
    el.scrollLeft = next;
  };

  const endDrag = () => {
    drag.current.down = false;
    drag.current.captured = false;
    drag.current.resumeAt = performance.now() + 1500;
  };

  const openImage = (idx) => {
    if (drag.current.moved) return;
    setActive(idx % GALLERY.length);
  };

  const step = (dir) => setActive((v) => (v + dir + GALLERY.length) % GALLERY.length);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <section id="services" data-testid="services-section" className="relative py-10 lg:py-14 overflow-hidden">
      <div
        ref={trackRef}
        data-testid="gallery-carousel"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onPointerCancel={endDrag}
        onDragStart={(e) => e.preventDefault()}
        className="no-scrollbar flex w-full overflow-x-auto select-none cursor-grab active:cursor-grabbing"
        style={{ touchAction: "pan-x pan-y", WebkitOverflowScrolling: "touch", overscrollBehaviorX: "contain" }}
      >
        {[...GALLERY, ...GALLERY].map((item, i) => (
          <button
            key={i}
            type="button"
            onClick={() => openImage(i)}
            data-testid={`gallery-image-${i}`}
            className="group relative shrink-0 mr-6 overflow-hidden rounded-2xl cursor-pointer focus:outline-none focus-visible:outline-none"
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            <img
              src={item.src}
              alt={`${item.title} ${item.subtitle}`}
              loading="lazy"
              draggable={false}
              className="h-[360px] lg:h-[500px] w-auto object-cover pointer-events-none transition-transform duration-700 group-hover:scale-105"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#03101d]/85 via-[#03101d]/20 to-transparent p-4 pt-12 lg:p-6 lg:pt-16 opacity-100 lg:opacity-0 transition-opacity duration-500 group-hover:opacity-100">
              <h3 className="font-title text-base lg:text-2xl font-bold text-white">{item.title}</h3>
              <p className="font-body text-xs lg:text-sm font-light text-white/80">{item.subtitle}</p>
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-[#03060f]/90 p-4 backdrop-blur-md sm:p-8"
            data-testid="gallery-lightbox"
            onClick={() => setActive(null)}
          >
            <button
              type="button"
              onClick={() => setActive(null)}
              data-testid="gallery-lightbox-close"
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition-colors hover:bg-white/15"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              data-testid="gallery-lightbox-prev"
              className="absolute left-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition-colors hover:bg-white/15 sm:left-8"
              aria-label="Previous"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              data-testid="gallery-lightbox-next"
              className="absolute right-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition-colors hover:bg-white/15 sm:right-8"
              aria-label="Next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex max-h-[88vh] max-w-[92vw] flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[active].src}
                alt={`${GALLERY[active].title} ${GALLERY[active].subtitle}`}
                className="max-h-[76vh] w-auto rounded-2xl object-contain shadow-2xl shadow-black/50"
                data-testid="gallery-lightbox-image"
              />
              <div className="mt-5 text-center">
                <h3 className="font-title text-3xl font-bold text-white" data-testid="gallery-lightbox-title">
                  {GALLERY[active].title}
                </h3>
                <p className="mt-1 font-body text-base font-light text-white/80">
                  {GALLERY[active].subtitle}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
